import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Download, 
  Database, 
  Users, 
  Network, 
  Calendar,
  FileText,
  Trash2,
  Settings,
  CheckCircle,
  AlertCircle,
  Clock,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';

const DatabaseBackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [backupConfig, setBackupConfig] = useState({
    include_users: true,
    include_matrix: true,
    include_pif: true,
    include_marketplace: true,
    include_lead_capture: true,
    include_bootcamp: true,
    include_admin_products: true,
    include_holding_tank: true,
    include_wallets: true,
    include_transactions: true,
    format: 'json',
    compress: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadBackupHistory();
  }, []);

  const loadBackupHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/backup/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      } else {
        toast.error("Failed to load backup history");
      }
    } catch (error) {
      toast.error("Network error loading backups");
    } finally {
      setLoading(false);
    }
  };

  const createFullBackup = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/backup/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backupConfig)
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        toast.success(`Database backup created with ${data.backup.total_records} records`);
        loadBackupHistory();
      } else {
        toast.error(data.detail || "Failed to create backup");
      }
    } catch (error) {
      toast.error("Network error creating backup");
    } finally {
      setCreating(false);
    }
  };

  const createQuickBackup = async (type) => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/backup/quick/${type}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        toast.success(data.message);
        loadBackupHistory();
      } else {
        toast.error(data.detail || "Failed to create backup");
      }
    } catch (error) {
      toast.error("Network error creating backup");
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/backup/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("Backup downloaded successfully");
      } else {
        toast.error("Failed to download backup");
      }
    } catch (error) {
      toast.error("Network error downloading backup");
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this backup? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/backup/delete/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        toast.success("Backup deleted successfully");
        loadBackupHistory();
      } else {
        toast.error(data.detail || "Failed to delete backup");
      }
    } catch (error) {
      toast.error("Network error deleting backup");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Database Backup Manager</h2>
          <p className="text-muted-foreground">
            Create, schedule, and manage database backups for disaster recovery
          </p>
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={() => loadBackupHistory()}
            variant="outline"
            disabled={loading}
          >
            {loading ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-lg">
              <Users className="h-5 w-5 mr-2 text-blue-500" />
              Users & Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Backup all user accounts, profiles, and wallet information
            </p>
            <Button 
              onClick={() => createQuickBackup('users')}
              disabled={creating}
              className="w-full"
              variant="outline"
            >
              {creating ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Quick Backup Users
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-lg">
              <Network className="h-5 w-5 mr-2 text-green-500" />
              Matrix & Downlines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Backup matrix positions, PIF data, and downline structures
            </p>
            <Button 
              onClick={() => createQuickBackup('matrix')}
              disabled={creating}
              className="w-full"
              variant="outline"
            >
              {creating ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Quick Backup Matrix
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-lg">
              <Archive className="h-5 w-5 mr-2 text-purple-500" />
              Everything
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Complete database backup with all collections and data
            </p>
            <Button 
              onClick={() => createQuickBackup('everything')}
              disabled={creating}
              className="w-full"
              variant="outline"
            >
              {creating ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Complete Backup
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Custom Backup Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Custom Backup Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Users & Profiles</label>
              <Switch
                checked={backupConfig.include_users}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_users: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Matrix System</label>
              <Switch
                checked={backupConfig.include_matrix}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_matrix: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">PIF System</label>
              <Switch
                checked={backupConfig.include_pif}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_pif: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Marketplace</label>
              <Switch
                checked={backupConfig.include_marketplace}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_marketplace: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Lead Capture</label>
              <Switch
                checked={backupConfig.include_lead_capture}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_lead_capture: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Boot Camp</label>
              <Switch
                checked={backupConfig.include_bootcamp}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_bootcamp: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Admin Products</label>
              <Switch
                checked={backupConfig.include_admin_products}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_admin_products: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Holding Tank</label>
              <Switch
                checked={backupConfig.include_holding_tank}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_holding_tank: checked}))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Wallets</label>
              <Switch
                checked={backupConfig.include_wallets}
                onCheckedChange={(checked) => 
                  setBackupConfig(prev => ({...prev, include_wallets: checked}))
                }
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Export Format</label>
              <Select 
                value={backupConfig.format} 
                onValueChange={(value) => setBackupConfig(prev => ({...prev, format: value}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">JSON Only</SelectItem>
                  <SelectItem value="csv">CSV Only</SelectItem>
                  <SelectItem value="both">Both JSON & CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={backupConfig.compress}
                  onCheckedChange={(checked) => 
                    setBackupConfig(prev => ({...prev, compress: checked}))
                  }
                />
                <label className="text-sm font-medium">Compress (ZIP)</label>
              </div>
            </div>
          </div>

          <Button 
            onClick={createFullBackup}
            disabled={creating}
            className="w-full"
          >
            {creating ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
            Create Custom Backup
          </Button>
        </CardContent>
      </Card>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Backup History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Clock className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Loading backup history...</p>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8">
              <Database className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No backups found. Create your first backup above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <Archive className="h-4 w-4 text-blue-500" />
                        <span className="font-medium">{backup.filename}</span>
                      </div>
                      <Badge variant="secondary">{formatFileSize(backup.size)}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Created: {formatDate(backup.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadBackup(backup.filename)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteBackup(backup.filename)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Backup Instructions & Recovery
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">📥 Using Backups</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Download backups as ZIP files for easy storage</li>
                <li>• JSON format preserves exact data structure</li>
                <li>• CSV format allows spreadsheet analysis</li>
                <li>• Store backups in multiple locations (cloud storage recommended)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">🔄 Disaster Recovery</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Keep regular backups (daily recommended)</li>
                <li>• Test backup restoration procedures</li>
                <li>• Maintain off-site backup copies</li>
                <li>• Document recovery procedures for your team</li>
              </ul>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-800">Important Notes</h4>
                <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                  <li>• Backups contain sensitive member data - store securely</li>
                  <li>• Large databases may take several minutes to backup</li>
                  <li>• Schedule backups during low-activity periods</li>
                  <li>• Verify backup integrity by downloading and checking files</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseBackupManager;